import { ReactNode } from 'react';
import { Tabs } from '@mantine/core';
import * as styles from './UnderlineTabs.css';

export type UnderlineTab<T extends string> = {
  value: T;
  /** @desc Figma's label, passed in ("By Brand", "Account Details"). */
  label: ReactNode;
  disabled?: boolean;
};

export type UnderlineTabsProps<T extends string> = {
  tabs: ReadonlyArray<UnderlineTab<T>>;
  value: T;
  onChange: (value: T) => void;
  /** @desc Names the tablist for assistive tech ("Account sections"). */
  'aria-label'?: string;
  /** @desc Keep inactive panels mounted (Mantine's default). Off for heavy panels. */
  keepMounted?: boolean;
  /** @desc `UnderlineTabs.Panel` elements, when the tabs own their content. */
  children?: ReactNode;
};

/**
 * @desc Figma's equal-width underline tabs (O3, R2, A2): a 4px navy indicator on a light track,
 * spanning the content width. Mantine `Tabs` underneath, so the tablist is keyboard-accessible —
 * arrow keys move and activate, Home/End jump — and panels, when passed, are wired by `value`.
 * `onChange` is typed to the tab values; an unknown value (Mantine's `null` on deactivation) is
 * ignored, since these tabs always have a current one.
 */
export function UnderlineTabs<T extends string>({
  tabs,
  value,
  onChange,
  'aria-label': ariaLabel,
  keepMounted,
  children,
}: UnderlineTabsProps<T>) {
  return (
    <Tabs
      value={value}
      onChange={(next) => {
        const tab = tabs.find((candidate) => candidate.value === next);
        if (tab) onChange(tab.value);
      }}
      color="navy"
      keepMounted={keepMounted}
      classNames={{ root: styles.root, list: styles.list, tab: styles.tab }}
    >
      <Tabs.List grow aria-label={ariaLabel}>
        {tabs.map((tab) => (
          <Tabs.Tab key={tab.value} value={tab.value} disabled={tab.disabled}>
            {tab.label}
          </Tabs.Tab>
        ))}
      </Tabs.List>
      {children}
    </Tabs>
  );
}

UnderlineTabs.Panel = Tabs.Panel;
